// ============================================================================
// Keyboard Shortcuts Hook
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Custom hook for managing global keyboard shortcuts including
//              Cmd+K/Ctrl+K for opening the command palette.
// User Story: US6 (Keyboard Shortcuts)
// ============================================================================

import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface KeyboardShortcut {
  key: string;
  ctrlOrCmd: boolean;
  shift?: boolean;
  alt?: boolean;
  callback: () => void;
  description: string;
}

interface UseKeyboardShortcutsOptions {
  /** Whether shortcuts are enabled */
  enabled?: boolean;
}

/**
 * Hook for managing global keyboard shortcuts
 * 
 * Features:
 * - Cross-platform Cmd/Ctrl key detection
 * - Shortcut registration and cleanup
 * - Conflict prevention
 * - Event handler management
 */
export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[] = [],
  options: UseKeyboardShortcutsOptions = {}
) => {
  const { enabled = true } = options;
  const [registeredShortcuts, setRegisteredShortcuts] = useState<KeyboardShortcut[]>([]);

  // Register shortcuts
  const registerShortcuts = useCallback((newShortcuts: KeyboardShortcut[]) => {
    setRegisteredShortcuts(prev => [...prev, ...newShortcuts]);
  }, []);

  // Unregister shortcuts
  const unregisterShortcuts = useCallback((shortcutsToRemove: KeyboardShortcut[]) => {
    setRegisteredShortcuts(prev => 
      prev.filter(shortcut => !shortcutsToRemove.includes(shortcut))
    );
  }, []);

  // Key event handler
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Skip if user is typing in an input field
    const target = event.target as HTMLElement;
    const isInputField = target.tagName === 'INPUT' || 
                        target.tagName === 'TEXTAREA' || 
                        target.contentEditable === 'true';

    // Allow shortcuts in input fields only for specific keys (like Cmd+K)
    const allowedInInputs = ['k', 'K'];
    if (isInputField && !allowedInInputs.includes(event.key)) {
      return;
    }

    const allShortcuts = [...shortcuts, ...registeredShortcuts];

    for (const shortcut of allShortcuts) {
      const matchesKey = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const matchesCtrlOrCmd = shortcut.ctrlOrCmd === (event.ctrlKey || event.metaKey);
      const matchesShift = (shortcut.shift || false) === event.shiftKey;
      const matchesAlt = (shortcut.alt || false) === event.altKey;

      if (matchesKey && matchesCtrlOrCmd && matchesShift && matchesAlt) {
        event.preventDefault();
        shortcut.callback();
        break; // Only execute the first matching shortcut
      }
    }
  }, [enabled, shortcuts, registeredShortcuts]);

  // Set up event listener
  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    registerShortcuts,
    unregisterShortcuts,
    registeredShortcuts,
  };
};

/**
 * Hook specifically for managing the global search palette
 * 
 * Features:
 * - Cmd+K/Ctrl+K shortcut handling
 * - Search palette state management
 * - Auto-close on navigation
 */
export const useGlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);

  const openSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleSearch = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Global search keyboard shortcuts
  const searchShortcuts: KeyboardShortcut[] = [
    {
      key: 'k',
      ctrlOrCmd: true,
      callback: toggleSearch,
      description: 'Open command palette',
    },
    {
      key: 'Escape',
      ctrlOrCmd: false,
      callback: closeSearch,
      description: 'Close command palette',
    },
  ];

  useKeyboardShortcuts(searchShortcuts);

  return {
    isOpen,
    openSearch,
    closeSearch,
    toggleSearch,
  };
};

/**
 * Hook for enhanced keyboard shortcuts including vim-style navigation and help overlay
 * 
 * Features:
 * - "?" shortcut for help overlay
 * - Vim-style navigation (g+key combinations)
 * - Global navigation shortcuts
 * - Help modal state management
 */
export const useEnhancedKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [vimSequence, setVimSequence] = useState<string | null>(null);

  const openHelp = useCallback(() => {
    setShowHelp(true);
  }, []);

  const closeHelp = useCallback(() => {
    setShowHelp(false);
  }, []);

  const toggleHelp = useCallback(() => {
    setShowHelp(prev => !prev);
  }, []);

  // Handle vim-style sequences (g+key)
  const handleVimSequence = useCallback((key: string) => {
    const vimRoutes: Record<string, string> = {
      'd': '/dashboard',        // g+d = Dashboard
      'w': '/workspaces',       // g+w = Workspaces  
      'c': '/capacity-metrics', // g+c = Capacities
      // '/backup' is the consolidated Backup section (Spec 201); '/backup/monitoring' named a tab it does not have.
      'b': '/backup',           // g+b = Backups
      'a': '/alerts',           // g+a = Alerts
      // '/admin/settings' has no route (it landed on the 404 page); '/settings' is the settings route.
      's': '/settings',         // g+s = Settings
      'p': '/backup/plans',  // g+p = Policies
    };

    const route = vimRoutes[key.toLowerCase()];
    if (route) {
      navigate(route);
      setVimSequence(null); // Clear the sequence
    } else {
      setVimSequence(null); // Clear invalid sequence
    }
  }, [navigate]);

  // Enhanced shortcuts including help and vim navigation
  const enhancedShortcuts: KeyboardShortcut[] = [
    // Help overlay
    {
      key: '?',
      ctrlOrCmd: false,
      shift: true, // Shift+? = ?
      callback: toggleHelp,
      description: 'Show keyboard shortcuts help',
    },
    // Vim-style navigation starter
    {
      key: 'g',
      ctrlOrCmd: false,
      callback: () => setVimSequence('g'),
      description: 'Start vim navigation sequence (g+key)',
    },
    // Direct navigation shortcuts
    {
      key: '1',
      ctrlOrCmd: true,
      callback: () => navigate('/dashboard'),
      description: 'Go to Dashboard',
    },
    {
      key: '2',
      ctrlOrCmd: true,
      callback: () => navigate('/workspaces'),
      description: 'Go to Workspaces',
    },
    {
      key: '3',
      ctrlOrCmd: true,
      callback: () => navigate('/backup'),
      description: 'Go to Backup Monitoring',
    },
    {
      key: '4',
      ctrlOrCmd: true,
      callback: () => navigate('/alerts'),
      description: 'Go to Alerts',
    },
  ];

  // Handle vim sequences separately
  const handleVimKeyDown = useCallback((event: KeyboardEvent) => {
    if (vimSequence === 'g') {
      event.preventDefault();
      handleVimSequence(event.key);
    }
  }, [vimSequence, handleVimSequence]);

  // Clear vim sequence after timeout
  useEffect(() => {
    if (vimSequence) {
      const timeout = setTimeout(() => {
        setVimSequence(null);
      }, 2000); // Clear after 2 seconds of inactivity

      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [vimSequence]);

  // Set up vim sequence handler
  useEffect(() => {
    if (vimSequence && typeof window !== 'undefined') {
      window.addEventListener('keydown', handleVimKeyDown);
      return () => window.removeEventListener('keydown', handleVimKeyDown);
    }
    return undefined;
  }, [handleVimKeyDown, vimSequence]);

  // Use the keyboard shortcuts
  useKeyboardShortcuts(enhancedShortcuts);

  // Get all available shortcuts for help display
  const getAllShortcuts = useCallback(() => {
    const vimShortcuts: KeyboardShortcut[] = [
      { key: 'g+d', ctrlOrCmd: false, callback: () => {}, description: 'Go to Dashboard' },
      { key: 'g+w', ctrlOrCmd: false, callback: () => {}, description: 'Go to Workspaces' },
      { key: 'g+c', ctrlOrCmd: false, callback: () => {}, description: 'Go to Capacities' },
      { key: 'g+b', ctrlOrCmd: false, callback: () => {}, description: 'Go to Backups' },
      { key: 'g+a', ctrlOrCmd: false, callback: () => {}, description: 'Go to Alerts' },
      { key: 'g+s', ctrlOrCmd: false, callback: () => {}, description: 'Go to Settings' },
      { key: 'g+p', ctrlOrCmd: false, callback: () => {}, description: 'Go to Policies' },
    ];

    const searchShortcuts: KeyboardShortcut[] = [
      { key: 'Cmd+K', ctrlOrCmd: true, callback: () => {}, description: 'Open command palette' },
      { key: 'Ctrl+K', ctrlOrCmd: true, callback: () => {}, description: 'Open command palette (Windows/Linux)' },
    ];

    return {
      navigation: [...enhancedShortcuts, ...vimShortcuts],
      search: searchShortcuts,
      help: [{ key: 'Shift+?', ctrlOrCmd: false, callback: () => {}, description: 'Show/hide this help' }],
    };
  }, [enhancedShortcuts]);

  return {
    showHelp,
    openHelp,
    closeHelp,
    toggleHelp,
    vimSequence,
    getAllShortcuts,
  };
};